class DeferredMedia extends HTMLElement {
  isPlaying = false;
  mediaPoster = null;
  toggleMediaButton = null;

  connectedCallback() {
    this.mediaPoster = this.querySelector('[data-ref="deferred-media-poster"]');
    this.toggleMediaButton = this.querySelector('[data-ref="toggle-media-button"]');

    this.mediaPoster?.addEventListener('click', this.showDeferredMedia.bind(this));
    this.toggleMediaButton?.addEventListener('click', this.toggleMedia.bind(this));

    this.setupLazyVideo();
  }

  /* Vidéo autoplay différée (performance mobile) : la balise <video> est
     rendue sans source par snippets/video.liquid (preload="none"). On
     n'attache la rendition qu'à l'approche du viewport, 720p maximum sur
     mobile, qualité maximale sur desktop. Rendu identique à l'écran. */
  setupLazyVideo() {
    const video = this.querySelector('video[data-mk-lazy]');
    if (!video || video.dataset.mkLoaded) return;

    const load = () => {
      if (video.dataset.mkLoaded) return;
      video.dataset.mkLoaded = 'true';

      const sources = Array.from(video.querySelectorAll('source[data-src]'));
      if (!sources.length) return;

      const mobile = window.matchMedia('(max-width: 750px)').matches;
      const maxHeight = mobile ? 720 : Infinity;
      const byQuality = sources
        .slice()
        .sort((a, b) => Number(b.dataset.height || 0) - Number(a.dataset.height || 0));
      const chosen = byQuality.find((s) => Number(s.dataset.height || 0) <= maxHeight) || byQuality[byQuality.length - 1];

      /* Liquid ne fournit que la rendition la plus haute : sur mobile on
         tente l'URL 720p dérivée (data-mk-hd720) et on revient à la source
         d'origine si le CDN ne la sert pas. (data-mk-hd720 : un tiret suivi d'un
         chiffre n'est pas converti en camelCase par dataset.) */
      const fallbackUrl = chosen.dataset.src;
      const primaryUrl = mobile && chosen.dataset.mkHd720 ? chosen.dataset.mkHd720 : fallbackUrl;
      sources.forEach((s) => s.remove());

      const start = (url) => {
        video.src = url;
        video.preload = 'auto';
        video.load();
        const playing = video.play();
        if (playing && playing.catch) playing.catch(() => {});
      };

      if (primaryUrl !== fallbackUrl) {
        video.addEventListener(
          'error',
          () => {
            if (video.src !== fallbackUrl) start(fallbackUrl);
          },
          { once: true },
        );
      }

      start(primaryUrl);
      this.isPlaying = true;
    };

    if (!('IntersectionObserver' in window)) {
      load();
      return;
    }

    const observer = new IntersectionObserver(
      (entries) => {
        if (entries.some((entry) => entry.isIntersecting)) {
          observer.disconnect();
          load();
        }
      },
      { rootMargin: '400px 0px' },
    );
    observer.observe(this);
  }

  disconnectedCallback() {}

  updatePlayPauseHint(isPlaying) {
    if (this.toggleMediaButton instanceof HTMLElement) {
      this.toggleMediaButton.classList.remove('hidden');

      const playIcon = this.toggleMediaButton.querySelector('.icon-play');
      if (playIcon) playIcon.classList.toggle('hidden', isPlaying);

      const pauseIcon = this.toggleMediaButton.querySelector('.icon-pause');
      if (pauseIcon) pauseIcon.classList.toggle('hidden', !isPlaying);
    }
  }

  showDeferredMedia = () => {
    this.loadContent(true);
    this.isPlaying = true;
    this.updatePlayPauseHint(this.isPlaying);
  };

  loadContent(focus = true) {
    if (this.getAttribute('data-media-loaded')) return;

    const content = this.querySelector('template')?.content.firstElementChild?.cloneNode(true);

    if (!content) return;

    this.setAttribute('data-media-loaded', 'true');

    this.querySelector('[data-ref="deferred-media-video"]')?.appendChild(content);

    // this.appendChild(content);

    if (focus && content instanceof HTMLElement) {
      content.focus();
    }

    this.querySelector('[ref="deferred-media-poster"]')?.classList.add('deferred-media__playing');

    if (content instanceof HTMLVideoElement && content.getAttribute('autoplay')) {
      // force autoplay for safari
      content.play();
    }
  }

  toggleMedia() {
    if (this.isPlaying) {
      this.pauseMedia();
    } else {
      this.playMedia();
    }
  }

  playMedia() {
    /** @type {HTMLIFrameElement | null} */
    const iframe = this.querySelector('iframe[data-video-type]');
    if (iframe) {
      iframe.contentWindow?.postMessage(
        iframe.dataset.videoType === 'youtube'
          ? '{"event":"command","func":"playVideo","args":""}'
          : '{"method":"play"}',
        '*',
      );
    } else {
      this.querySelector('video')?.play();
    }
    this.isPlaying = true;
    this.updatePlayPauseHint(this.isPlaying);
  }

  pauseMedia() {
    /** @type {HTMLIFrameElement | null} */
    const iframe = this.querySelector('iframe[data-video-type]');

    if (iframe) {
      iframe.contentWindow?.postMessage(
        iframe.dataset.videoType === 'youtube'
          ? '{"event":"command","func":"' + 'pauseVideo' + '","args":""}'
          : '{"method":"pause"}',
        '*',
      );
    } else {
      this.querySelector('video')?.pause();
    }
    this.isPlaying = false;

    // If we've already revealed the deferred media, we should toggle the play/pause hint
    if (this.getAttribute('data-media-loaded')) {
      this.updatePlayPauseHint(this.isPlaying);
    }
  }
}

if (!customElements.get('deferred-media')) {
  customElements.define('deferred-media', DeferredMedia);
}
